// b20-2 (BK52): ytterdörren i Stugkoll. Kortet Ytterdörr på Hälsa för ett hus: låst, olåst eller
// regeln ute med en symbol, dörren, batteriet, den senaste ändringen och historiken de senaste 7
// dygnen, med källan i liten text. Bara läsning: kortet har ingen knapp för att låsa upp, bara
// raden "Styrning av låset är inte påslagen".
//
// Under Inställningar → Ytterdörren: incheckningen, utcheckningen och när
// städningen är klar efter en egen vistelse och efter gäster, och vistelserna, som ägaren lägger in
// med ankomst och avresa. Notiserna om en olåst dörr utgår från vistelserna och städfönstren.

import {
  klockslagI,
  type Platsinstallningar,
  type Vistelse,
  type Vistelsetyp,
  type Ytterdorr,
} from "@batkoll/gemensam";
import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import { api, Felsvar } from "../api.ts";
import { Kort, Rad } from "../komponenter.tsx";

const LAGE: Record<string, string> = {
  last: "Låst",
  olast: "Olåst",
  regel: "Låst med regeln ute",
  okand: "Okänt",
};

/** Ett hänglås, stängt eller öppet, eller ett frågetecken när läget är okänt. */
function Lassymbol(props: { lage: string }) {
  const oppet = props.lage === "olast";
  if (props.lage !== "last" && props.lage !== "regel" && !oppet) {
    return (
      <svg class="lassymbol" viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2" />
        <text x="12" y="17" text-anchor="middle" font-size="14" fill="currentColor">
          ?
        </text>
      </svg>
    );
  }
  return (
    <svg class="lassymbol" viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
      <path
        d={oppet ? "M7 11V7a5 5 0 0 1 9.6-2" : "M7 11V7a5 5 0 0 1 10 0v4"}
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      />
      <rect x="4" y="11" width="16" height="11" rx="2" fill="currentColor" />
    </svg>
  );
}

/** Dag och klockslag i platsens tidszon, till exempel "27 okt. 11.04". */
function dagTid(ms: number, tidszon: string): string {
  const d = new Date(ms).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
    timeZone: tidszon,
  });
  return `${d} ${klockslagI(ms, tidszon)}`;
}

/** Kortet Ytterdörr på Hälsa. */
export function Ytterdorrkort(props: { y: Ytterdorr; tidszon: string }) {
  const y = props.y;
  const lage = LAGE[y.lage] ?? "Okänt";
  return (
    <Kort rubrik="Ytterdörr">
      <div class="stort mellan">
        <Lassymbol lage={y.lage} />
        {lage}
      </div>
      {y.dorrLases ? (
        <Rad namn="Dörren">{y.oppen === null ? "–" : y.oppen ? "Öppen" : "Stängd"}</Rad>
      ) : (
        <p class="rad dorr-lases-inte">Dörren: läses inte</p>
      )}
      <Rad namn="Batteriet">{y.batteri === null ? "–" : `${y.batteri} %`}</Rad>
      {y.senast && <Rad namn="Senaste ändringen">{y.senast.text}</Rad>}
      {y.kontakt === "borta" && (
        <p class="meddelande">Låset har inte nåtts på 30 minuter. Läget är det senast kända.</p>
      )}
      {y.historik.length > 0 && (
        <details class="lashistorik">
          <summary>De senaste 7 dygnen ({y.historik.length})</summary>
          <ol class="logg">
            {y.historik.slice(0, 100).map((r) => (
              <li key={`${r.tid}${r.text}`}>
                <time dateTime={new Date(r.tid).toISOString()}>{dagTid(r.tid, props.tidszon)}</time>{" "}
                {r.text}
              </li>
            ))}
          </ol>
        </details>
      )}
      <small class="ute-kalla">{y.kalla}</small>
      <p class="liten">Styrning av låset är inte påslagen.</p>
    </Kort>
  );
}

type Spara = (
  sektion: string,
  gor: () => Promise<Platsinstallningar>,
  ok?: string,
) => Promise<boolean>;

/** Timmar som "17:00" för ett tidsfält, och tillbaka. */
const somKlocka = (t: number) => {
  const min = Math.round(t * 60);
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
};
/** Timmar som klockslag i en text: "17.00". */
const kl = (t: number) => somKlocka(t).replace(":", ".");
const somTimmar = (s: string): number | null => {
  const m = /^(\d{1,2})[:.](\d{2})$/.exec(s.trim());
  if (!m) return null;
  return Number(m[1]) + Number(m[2]) / 60;
};

/** "2/11–5/11" i platsens tidszon. */
function datum(v: Vistelse, tidszon: string): string {
  const d = (ms: number) =>
    new Date(ms).toLocaleDateString("sv-SE", {
      day: "numeric",
      month: "numeric",
      timeZone: tidszon,
    });
  return `${d(v.start)}–${d(v.slut)}`;
}

const TYPTEXT: Record<Vistelsetyp, string> = { egen: "Egen vistelse", gast: "Gäster" };

/** Inställningar → Ytterdörren, med vistelserna, för ett hus. */
export function Ytterdorrdel(props: {
  s: Platsinstallningar;
  spara: Spara;
  beskedrad: ComponentChildren;
  nu: number;
}) {
  const g = props.s.ytterdorr;
  const id = props.s.plats.id;
  const tidszon = props.s.plats.tidszon;
  const [tider, setTider] = useState({
    incheckning: somKlocka(g?.incheckning.varde ?? 17),
    utcheckning: somKlocka(g?.utcheckning.varde ?? 11),
    stadEgen: somKlocka(g?.stadEgen.varde ?? 13),
    stadGast: somKlocka(g?.stadGast.varde ?? 17),
  });
  const [vistelser, setVistelser] = useState<Vistelse[] | null>(null);
  const [ny, setNy] = useState({ ankomst: "", avresa: "", typ: "gast" as Vistelsetyp });
  const [fel, setFel] = useState<string | null>(null);

  useEffect(() => {
    if (!g) return;
    api
      .vistelser(id)
      .then(setVistelser)
      .catch(() => setFel("Vistelserna gick inte att hämta."));
  }, [id, g === null || g === undefined]);

  if (!g) return null;
  const installd = [g.incheckning, g.utcheckning, g.stadEgen, g.stadGast].some((x) => x.installd);

  function sparaTider(e: Event) {
    e.preventDefault();
    void props.spara("ytterdorr", async () => {
      const v = {
        incheckning: somTimmar(tider.incheckning),
        utcheckning: somTimmar(tider.utcheckning),
        stadEgen: somTimmar(tider.stadEgen),
        stadGast: somTimmar(tider.stadGast),
      };
      if (Object.values(v).some((x) => x === null))
        throw new Felsvar("Skriv tiderna som klockslag, till exempel 17:00.", 400);
      return api.andraPlats(id, { ytterdorr: v });
    });
  }

  async function laggTill(e: Event) {
    e.preventDefault();
    setFel(null);
    try {
      setVistelser(await api.laggTillVistelse(id, ny));
      setNy({ ankomst: "", avresa: "", typ: ny.typ });
    } catch (x) {
      setFel(x instanceof Felsvar ? x.message : "Vistelsen gick inte att lägga in.");
    }
  }

  async function taBort(v: string) {
    setFel(null);
    try {
      setVistelser(await api.taBortVistelse(id, v));
    } catch (x) {
      setFel(x instanceof Felsvar ? x.message : "Vistelsen gick inte att ta bort.");
    }
  }

  const tid = (etikett: string, nyckel: keyof typeof tider) => (
    <label>
      {etikett}
      <input
        type="time"
        step={900}
        value={tider[nyckel]}
        onInput={(e) => setTider({ ...tider, [nyckel]: e.currentTarget.value })}
      />
    </label>
  );

  return (
    <Kort rubrik="Ytterdörren" bred>
      <form class="formular" onSubmit={sparaTider}>
        {tid("Incheckning", "incheckning")}
        {tid("Utcheckning", "utcheckning")}
        {tid("Städningen klar efter en egen vistelse", "stadEgen")}
        {tid("Städningen klar efter gäster", "stadGast")}
        <p class="liten">
          Under en vistelse och i städfönstret, från utcheckningen tills städningen är klar, kommer
          ingen notis om en olåst dörr. Efter städfönstret kommer en varning när dörren har varit
          olåst i 15 minuter. Förval: incheckning {kl(g.incheckning.forval)}, utcheckning{" "}
          {kl(g.utcheckning.forval)}, och städningen klar {kl(g.stadEgen.forval)} efter en egen
          vistelse och {kl(g.stadGast.forval)} efter gäster{installd ? ". Nu inställda." : "."}
        </p>
        {props.beskedrad}
        <div class="knapprad">
          <button class="knapp-huvud" type="submit">
            Spara tiderna
          </button>
          {installd && (
            <button
              type="button"
              class="knapp-andra"
              onClick={() =>
                props.spara("ytterdorr", () =>
                  api.andraPlats(id, {
                    ytterdorr: {
                      incheckning: null,
                      utcheckning: null,
                      stadEgen: null,
                      stadGast: null,
                    },
                  }),
                )
              }
            >
              Återställ till förvalen
            </button>
          )}
        </div>
      </form>

      <h3>Vistelser</h3>
      {vistelser === null ? (
        <p class="laddar">Hämtar …</p>
      ) : vistelser.length === 0 ? (
        <p class="meddelande">Inga vistelser inlagda.</p>
      ) : (
        <ul class="vistelser">
          {vistelser.map((v) => (
            <li key={v.id}>
              <span>
                {datum(v, tidszon)} · {TYPTEXT[v.typ]} · städat {klockslagI(v.stadSlut, tidszon)}
                {v.slut <= props.nu && v.stadSlut > props.nu ? " · städfönster nu" : ""}
                {v.start <= props.nu && props.nu < v.slut ? " · pågår" : ""}
              </span>{" "}
              {v.kalla === "agare" && (
                <button type="button" class="knapp-lank" onClick={() => void taBort(v.id)}>
                  Ta bort
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <form class="formular" onSubmit={laggTill}>
        <label>
          Ankomst
          <input
            type="date"
            required
            value={ny.ankomst}
            onInput={(e) => setNy({ ...ny, ankomst: e.currentTarget.value })}
          />
        </label>
        <label>
          Avresa
          <input
            type="date"
            required
            value={ny.avresa}
            onInput={(e) => setNy({ ...ny, avresa: e.currentTarget.value })}
          />
        </label>
        <label>
          Vem
          <select
            value={ny.typ}
            onChange={(e) => setNy({ ...ny, typ: e.currentTarget.value as Vistelsetyp })}
          >
            <option value="gast">Gäster</option>
            <option value="egen">Egen vistelse</option>
          </select>
        </label>
        {fel && (
          <p class="fel" role="alert">
            {fel}
          </p>
        )}
        <div class="knapprad">
          <button class="knapp-huvud" type="submit">
            Lägg in vistelsen
          </button>
        </div>
      </form>
    </Kort>
  );
}
